import { FC, Ref, useCallback, useEffect, useState } from "react";
import AppLayout from "../../components/layout/app-layout/app-layout";
import Tabs from "../../components/shared/Tabs";
import DataTable from "../../components/shared/DataTable/DataTable";
import { ServiceQuotation } from "../../services/service-quotation";
import { getDaysLeft } from "../../util";
import { RouteComponentProps, withRouter } from "react-router-dom";
import Modal from "../../components/shared/Modal/Modal";
import { ReactComponent as CircleQuestionIcon } from '../../assets/svg/circleQuestionIcon.svg';
import { ReactComponent as CircleXIcon } from '../../assets/svg/circleXIcon.svg';
import { ReactComponent as CircleCheckIcon } from '../../assets/svg/circleCheckIcon.svg';
import { BidIntentStatus } from "../page-quotation/BidIntentControls/BidIntentControls";
import TooltipContainer from "../../components/shared/TooltipContainer/TooltipContainer";
import Loading from "../../components/shared/Loading";
import type { Request } from "./types";
import { NotificationManager } from "react-notifications";
import appState from "../../state/AppStateContainer";
import OptionsMenu from "../../components/shared/OptionsMenu/OptionsMenu";
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Card from "../../components/shared/Card/card";
import BtButton from "../bt-button/bt-button";
import serviceSales from "../../services/service-sales";
import type { Subscription } from "../../services/service-sales";
import { formatDate } from '../../util';
import { useTranslation } from "react-i18next";
import { ReactComponent as ThunderIcon } from '../../assets/svg/thunderIcon.svg';
import { ReactComponent as EyeIcon } from '../../assets/svg/openedEyeIcon.svg';
import SimpleTaskCard from '../../components/shared/SimpleTaskCard/SimpleTaskCard';
import styles from '../../components/shared/SimpleTaskCard/SimpleTaskCard.module.scss';
import InfoBox from '../../components/shared/InfoBox/InfoBox';
import ReceivedBidRequestAllStats from "./ReceivedRequestsAllStats";

type Params = {};
type RouteTypes = RouteComponentProps<Params, Record<string, unknown>>;
type Props = {
  history: RouteTypes['history'];
}

export type Stats = {
  totalRequests: number,
  totalBids: number,
  won: number,
  ongoing: {
    ongoingUnanswered: number,
    ongoingAccepted: number,
    ongoingDeclined: number,
    ongoingBids: number,
  },
  recent: {
    recentUnanswered: number,
    recentAccepted: number,
    recentDeclined: number,
    recentBids: number,
  },
  older: {
    olderUnanswered: number,
    olderAccepted: number,
    olderDeclined: number,
    olderBids: number,
  },
  chartData: {
    week: string,
    requests: number,
    bids: number,
    isAfterAd: boolean,
  }[],
  topOrganizations: {
    key: string,
    value: number,
    name: string,
  }[],
}

const defaultStats: Stats = {
  totalRequests: 0,
  totalBids: 0,
  won: 0,
  ongoing: {
    ongoingUnanswered: 0,
    ongoingAccepted: 0,
    ongoingDeclined: 0,
    ongoingBids: 0,
  },
  recent: {
    recentUnanswered: 0,
    recentAccepted: 0,
    recentDeclined: 0,
    recentBids: 0,
  },
  older: {
    olderUnanswered: 0,
    olderAccepted: 0,
    olderDeclined: 0,
    olderBids: 0,
  },
  chartData: [],
  topOrganizations: [],
}

const PageBidRequestList : FC<Props> = ({ history }) => {
  const [t] = useTranslation('requests');
  const [selectedTab, setSelectedTab] = useState(0);
  const [requests, setRequests] = useState<Request[]>([]);
  const [stats, setStats] = useState<Stats>(defaultStats);
  const [addContactModalShow, setAddContactModalShow] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState("");
  const [showAllRequests, setShowAllRequests] = useState(false);
  const [isSubscriptionActive, setIsSubscriptionActive] = useState(false);
  const [superadminSubscriptionOverride, setSuperadminSubscriptionOverride] = useState<boolean>();
  const [publihedSubscription, setPublishedSubscription] = useState<Subscription>();
  const [isFetchingStats, setFetchingStats] = useState(false);
  const [isFetchingRequests, setFetchingRequests] = useState(false);

  useEffect(() => {
    (async () => {
      setFetchingStats(true);
      try {
        const publishedSubscriptions = await serviceSales.fetchPublishedQuotationsSubscription();
        setPublishedSubscription(publishedSubscriptions)
        const stats = await ServiceQuotation.fetchAllStats();
        setStats(stats as Stats);
      } catch (e) {
        NotificationManager.error((e as Error).toString(), t('notification.error', 'Kunde inte hämta förfrågningar'));
      }
      setFetchingStats(false);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setFetchingRequests(true);
      try {
        const response = await ServiceQuotation.fetchAllReceived(superadminSubscriptionOverride);
        setRequests(response.data);
        setIsSubscriptionActive(!!response.isSubscriptionActive);
        if (response.isSubscriptionActive) {
          setShowAllRequests(true);
        }
      } catch (e) {
        NotificationManager.error((e as Error).toString(), t('notification.error', 'Kunde inte hämta förfrågningar'));
      }
      setFetchingRequests(false);
    })();
  }, [superadminSubscriptionOverride]);

  const onRowClick = useCallback((_: React.MouseEvent | undefined, rowData: Request | undefined) => {
    if (rowData?.link) {
      window.open(rowData.link);
    } else if (rowData?.id) {
      setSelectedQuotation(rowData?.id);
      setAddContactModalShow(true);
    }
  }, []);

  const onCreateContactClick = async () => {
    const { link } = await ServiceQuotation.createContactForSalesOrgUserToOpenQuotation(selectedQuotation);
    window.open(link);
    setAddContactModalShow(false);
  }

  const columns = [
    {
      title: t('table.header.project', 'Projekt'),
      field: "project",
      render: (rowData: Request) => {
        return (
          <span style={{ color: rowData.isDeleted ? 'var(--gray-500)' : 'var(--gray-900)', fontSize: '16px', fontWeight: 500, textDecoration: rowData.isDeleted ? 'line-through' : '' }}>
            {rowData.project}
          </span>
        );
      },
    },
    {
      title: t('table.header.org', 'Beställare'),
      field: "org",
    },
    {
      title: t('table.header.category', 'Yrkeskategori'),
      field: "category",
    },
    {
      title: t('table.header.version', 'Version'),
      field: "version",
      render: (rowData: Request) => +rowData.version + 1,
    },
    {
      title: t('table.header.date', 'Skickad'),
      field: "date",
      render: (rowData: Request) => formatDate(rowData.date),
    },
    {
      title: t('table.header.timeLeft', 'Anbudstid'),
      field: "timeLeft",
      render: (rowData: Request) => {
        const daysLeft = getDaysLeft(rowData.timeLeft);
        return (
          <span style={{ whiteSpace: 'nowrap' }}>{
            isNaN(+daysLeft) ? daysLeft : t('table.row.timeLeft', '{{count}} dagar', { context: +daysLeft < 0 ? 'expired' : null, count: +daysLeft })
          }</span>
        );
      }
    },
    {
      title: t('table.header.deliveryStart', 'Leveransdatum'),
      field: "deliveryStart",
      render: (rowData: Request) => {
        const d1 = formatDate(rowData.deliveryStart);
        const d2 = formatDate(rowData.deliveryEnd);
        return (!d1 && !d2) ? '–' : [d1, d2].filter(x=>x).join(' – ');
      }
    },
    {
      title: t('table.header.recipients', 'Mottagare'),
      field: "recipients",
      render: (rowData: Request) => rowData.recipients.join(", "),
    },
    {
      title: t('table.header.intent', 'Avsikt'),
      field: "intent",
      render: (rowData: Request) => {
        const icons = {
          [BidIntentStatus.UNDECIDED]: (className: string, ref: Ref<SVGSVGElement>) => <CircleQuestionIcon fill='var(--gray-700)' width='16px' height='16px' className={className} ref={ref} />,
          [BidIntentStatus.ACCEPTED]: (className: string, ref: Ref<SVGSVGElement>) => <CircleCheckIcon fill='var(--gray-700)' width='16px' height='16px' className={className} ref={ref} />,
          [BidIntentStatus.DECLINED]: (className: string, ref: Ref<SVGSVGElement>) => <CircleXIcon fill='var(--gray-700)' width='16px' height='16px' className={className} ref={ref} />,
        };
        const messages = {
          [BidIntentStatus.UNDECIDED]: t('table.row.intent.undecided', 'Ingen avsiktsförklaring'),
          [BidIntentStatus.ACCEPTED]: t('table.row.intent.accepted', 'Avser att lämna anbud'),
          [BidIntentStatus.DECLINED]: t('table.row.intent.declined', 'Avser att inte lämna anbud'),
        };
        if (rowData.intent) {
          const intent : BidIntentStatus = rowData.intent;
          return (
            <TooltipContainer className='bidIntentTooltip' renderReferenceComponent={icons[intent]}>
              <div><p>{messages[intent]}</p></div>
            </TooltipContainer>
          );
        } else {
          return "–";
        }
      },
    },
    {
      title: t('table.header.answered', 'Anbud lämnat'),
      field: "hasBid",
      render: (rowData: Request) => rowData.hasBid ? t('table.row.yes', 'Ja') : t('table.row.no', 'Nej')
    },
    {
      title: t('table.header.won', 'Vunnen'),
      field: "won",
      render: (rowData: Request) => rowData.won ? t('table.row.yes', 'Ja') : t('table.row.no', 'Nej')
    },
  ];

  const now = new Date();
  const requestsToUser = requests.filter(r => r.recipients.includes(appState.getUserEmail()));
  const inProgressNoBid = (showAllRequests ? requests : requestsToUser).filter((r) => new Date(r.timeLeft) >= now && !r.answered);
  const inProgressHasBid = (showAllRequests ? requests : requestsToUser).filter((r) => new Date(r.timeLeft) >= now && r.answered);
  const expired = (showAllRequests ? requests : requestsToUser).filter((r) => new Date(r.timeLeft) < now);
  const filtered = selectedTab == 1 ? inProgressNoBid : selectedTab == 2 ? inProgressHasBid : expired;

  let options;
  let selectedOption;
  const hasActiveSubscription = (isSubscriptionActive && superadminSubscriptionOverride !== false) || superadminSubscriptionOverride;
  const hasPublishedSubscription = !!publihedSubscription?.active?.startDate;
  const optionLabels = {
    allRequests: t('displayOptions.allRequests', 'alla förfrågningar'),
    myRequests: t('displayOptions.myRequests', 'mina förfrågningar')
  }
  if (hasActiveSubscription) {
    options = [
      { label: optionLabels.allRequests, action: () => setShowAllRequests(true) },
      { label: optionLabels.myRequests, action: () => setShowAllRequests(false) }
    ];
    selectedOption = options[showAllRequests ? 0 : 1].label;
  } else {
    options = [{ label: selectedTab === 0 ? optionLabels.allRequests : optionLabels.myRequests, action: () => {} }];
    selectedOption = options[0].label;
  }

  const loading = isFetchingStats && isFetchingRequests;

  return (
    <AppLayout title={t('pageTitle', 'Mottagna förfrågningar')}>
      <div style={{margin: "32px"}}>
        <div className="tab-container">
          <Tabs
            tabs={[
              t('tabs.overview', 'Översikt'),
              t('tabs.noBid', 'Obesvarade ({{count}})', { count: inProgressNoBid.length }),
              t('tabs.hasBid', 'Besvarade ({{count}})', { count: inProgressHasBid.length }),
              t('tabs.expired', 'Avslutade ({{count}})', { count: expired.length })
            ]}
            selected={selectedTab}
            onChange={setSelectedTab}
          />
          <div className="tab-actions" style={{paddingTop: "16px"}}>
            <OptionsMenu
              selectedItem={selectedOption}
              options={options}
              style={{fontSize: "14px", color: "var(--gray-700)"}}>
                <div>
                  {t('displayOptions.label', 'Visa')}
                  <strong style={{ color: "var(--gray-900)", fontWeight: 500 }}> {selectedOption}</strong>
                  <ExpandMoreIcon fontSize="small" style={{verticalAlign: "bottom"}}/>
                </div>
            </OptionsMenu>
          </div>
        </div>
        
        {!loading && (<>
          <div className={styles.taskCardsContainer}>
            {!hasActiveSubscription && (<SimpleTaskCard
              title={t('taskCard.prio.title', 'Få fler förfrågningar med Accurator Prio')}
              description={t('taskCard.prio.description', 'Bli rekommenderad leverantör, se alla företagets förfrågningar i en vy och följ resultatet i dashboarden. Publicerade förfrågningar ingår.')}
              buttonText={t('taskCard.prio.button', 'Öka synligheten')}
              icon={ThunderIcon}
              buttonIcon={EyeIcon}
              redirectTo="/marketing"
              notFill={true}
            />)}

            {!(hasActiveSubscription || hasPublishedSubscription) && (<SimpleTaskCard
              title={t('taskCard.published.title', 'Sök bland publicerade förfrågningar')}
              description={t('taskCard.published.description', 'Sök och filtrera öppna upphandlingar där inköpare aktivt söker nya leverantörer. Skapa bevakningar och lämna anbud direkt.')}
              buttonText={t('taskCard.published.button', 'Visa')}
              icon={EyeIcon}
              redirectTo="/published-requests"
            />)}
          </div>

          {!hasActiveSubscription && selectedOption === optionLabels.allRequests && selectedTab === 0 && (<InfoBox
            description={t('infoBox.description', 'Statistiken nedan är baserad på förfrågningar till hela ert företag, men på de andra flikarna visas bara förfrågningar skickade direkt till din  e-postadress.')}
            customStyle={{ width: 'calc(99% - 16px)', marginBottom: '16px' }}
          />)}
        </>)}
        
        {selectedTab == 0 ? (
          loading ? (
            <Loading type='inline' />
          ) : (<>
            <ReceivedBidRequestAllStats stats={stats} />

            {appState.isSuperAdmin() && (
              <Card title="Superadmin" style={{ width: "calc(99% - 16px)" }}>
                <div style={{display:"flex", gap: "12px", fontSize: "12px", color: "var(--gray-700)", alignItems: "center"}}>
                  {superadminSubscriptionOverride === true || superadminSubscriptionOverride === false ? (<>
                    {t('superadmin.subscriptionOverride.status', 'Prenumerationsstatus lokalt ändrad i denna session. Prenumerationen är på', { context: superadminSubscriptionOverride ? 'on' : 'off'})}
                    <BtButton size="xxs" color="white" onClick={() => {
                      setSuperadminSubscriptionOverride(undefined);
                      setShowAllRequests(isSubscriptionActive);
                    }}>
                      {t('superadmin.subscriptionOverride.button.reset', 'Återställ')}
                    </BtButton>
                  </>) : (<>
                    {t('superadmin.subscriptionOverride.intro', 'Ändra prenumerationsstatus lokalt i denna session.')}
                    <BtButton size="xxs" color="white" onClick={() => {
                      setSuperadminSubscriptionOverride(true);
                      setShowAllRequests(true);
                    }}>
                      {t('superadmin.subscriptionOverride.button.on', 'Prenumeration på')}
                    </BtButton>
                    <BtButton size="xxs" color="white" onClick={() => {
                      setSuperadminSubscriptionOverride(false);
                      setShowAllRequests(false);
                    }}>
                      {t('superadmin.subscriptionOverride.button.off', 'Prenumeration av')}
                    </BtButton>
                  </>)}
                </div>
              </Card>
            )}
          </>)
        ) : (
          <div>
            <DataTable columns={columns} data={filtered} isLoading={loading} onRowClick={onRowClick} pageSize={10} isScrollable style={{ maxHeight: "calc(100vh - 300px)" }} />
          </div>
        )}
      </div>
      <Modal
        title={t('addContact.heading', 'Lägg till kontaktperson?')}
        show={addContactModalShow}
        setShow={setAddContactModalShow}
        buttonInfo={{label: t('addContact.button.add', 'Ja'), action: onCreateContactClick}}
        cancelButtonText={t('addContact.button.cancel', 'Nej')}
      >
        <div style={{fontSize: "14px", color: "var(--gray-700)", maxWidth: "500px"}}>
          {t('addContact.intro', 'För att öppna denna förfrågan måste du vara kontaktperson för denna leverantör i beställarens leverantörsregister. Vill du läggas till som kontaktperson?')}
        </div>
      </Modal>
    </AppLayout>
  );
}

export default withRouter(PageBidRequestList);
